import { Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import { GeckoFeature, GeckoMeasurement } from 'types'

import { FeatureCylinder3D } from './FeatureCylinder3D'

/**
 * A ring of bolts, usually at the end of a manway or nozzle. Bolts are given
 * @param feature       The feature these bolts belong to
 * @param position      The position of the center of this bolt ring in space
 * @param width         The width of the rectangle along which to place the bolts
 * @param height        The height of the rectangle along which to place the bolts
 * @param scale         Optional. Number to scale the size/radius of the bolts. Default is 1.
 *                      Default radius of each bolt is 1cm.
 * @constructor
 */
export const FeatureBoltRectangle3D: React.FC<{
  feature: GeckoFeature
  center: Vec3
  width: GeckoMeasurement
  height: GeckoMeasurement
  scale?: number
}> = ({ feature, center, width, height, scale = 1 }) => {
  //----------------------------------------------------------------------------

  const { boltPositions } = React.useMemo(() => {
    const boltRadius = scale

    const widthInInches = width.num //toNumber(GeckoMeasurementUnit.Inches)
    const heightInInches = height.num //toNumber(GeckoMeasurementUnit.Inches)

    const defaultBoltSpacing = boltRadius * 0.1

    const boltsAlongX = Math.ceil(widthInInches / defaultBoltSpacing)
    const boltsAlongY = Math.ceil(heightInInches / defaultBoltSpacing)

    const boltSpacing = [
      widthInInches / boltsAlongX,
      heightInInches / boltsAlongY,
    ]

    const boltPositions: Array<Vec3> = []
    for (let i = 0; i <= boltsAlongX; i++) {
      for (let j = 0; j <= boltsAlongY; j++) {
        // only place bolts on the edges
        if (!(i === 0 || i === boltsAlongX) && !(j === 0 || j === boltsAlongY))
          continue
        boltPositions.push([
          i * boltSpacing[0] - widthInInches / 2,
          0,
          j * boltSpacing[1] - heightInInches / 2,
        ])
      }
    }
    return { boltPositions }
  }, [width, height, scale])

  return (
    <group position={center}>
      {boltPositions.map((position, i) => (
        <FeatureCylinder3D
          key={`bolt_${i}`}
          feature={feature}
          position={position}
          radius={0.01 * scale}
          height={0.01 * scale}
          debugColor={'green'}
        />
      ))}
    </group>
  )
}
